/**
 * FikoMeasurementArc - Next-Gen 3D Arc Measurement System
 *
 * Gelişmiş yay çizim ve ölçüm sistemi
 * - Çoklu yay stilleri (solid, dashed, gradient, animated)
 * - Açı gösterimi (degree, radian, gradian)
 * - Animasyon efektleri (pulse, sweep, glow)
 * - Interaktif özellikler (hover, select, drag)
 * - Camera-aware rendering
 * - GPU-optimized shader effects
 *
 * @author FikoYT Advanced CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * Yay çizim stilleri
 */
export enum ArcStyleType {
    // Temel stiller
    SOLID = 'SOLID',
    DASHED = 'DASHED',
    DOTTED = 'DOTTED',

    // Gradient stiller
    GRADIENT_LINEAR = 'GRADIENT_LINEAR',
    GRADIENT_RADIAL = 'GRADIENT_RADIAL',
    GRADIENT_RAINBOW = 'GRADIENT_RAINBOW',

    // Animasyonlu stiller
    ANIMATED_FLOW = 'ANIMATED_FLOW',
    ANIMATED_PULSE = 'ANIMATED_PULSE',
    ANIMATED_SWEEP = 'ANIMATED_SWEEP',

    // Özel efektler
    NEON_GLOW = 'NEON_GLOW',
    HOLOGRAPHIC = 'HOLOGRAPHIC',
    PLASMA = 'PLASMA',
    ELECTRIC = 'ELECTRIC',
    LASER = 'LASER',

    // Profesyonel CAD stilleri
    CATIA_STANDARD = 'CATIA_STANDARD',
    SOLIDWORKS_STANDARD = 'SOLIDWORKS_STANDARD',
    AUTOCAD_STANDARD = 'AUTOCAD_STANDARD',

    // Geometrik stiller
    WAVE_SINE = 'WAVE_SINE',
    WAVE_SQUARE = 'WAVE_SQUARE',
    ZIGZAG = 'ZIGZAG',
    SPIRAL = 'SPIRAL',

    // Dekoratif stiller
    CHAIN = 'CHAIN',
    ROPE = 'ROPE',
    BARBED = 'BARBED',

    // Teknik stiller
    CENTERLINE = 'CENTERLINE',
    PHANTOM = 'PHANTOM',
    CONSTRUCTION = 'CONSTRUCTION'
}

/**
 * Açı birimi
 */
export enum AngleUnit {
    DEGREES = 'DEGREES',
    RADIANS = 'RADIANS',
    GRADIANS = 'GRADIANS',
    TURNS = 'TURNS'
}

/**
 * Yay tipi
 */
export enum ArcType {
    ANGLE = 'ANGLE',           // Açı ölçümü
    ARC_LENGTH = 'ARC_LENGTH', // Yay uzunluğu
    SECTOR = 'SECTOR',         // Dairesel dilim
    SEGMENT = 'SEGMENT',       // Dairesel segment
    CHORD = 'CHORD',           // Kiriş
    RADIUS = 'RADIUS',         // Yarıçap gösterimi
    DIAMETER = 'DIAMETER'      // Çap gösterimi
}

/**
 * Animasyon tipi
 */
export enum ArcAnimationType {
    NONE = 'NONE',
    PULSE = 'PULSE',
    SWEEP = 'SWEEP',
    FLOW = 'FLOW',
    GLOW = 'GLOW',
    ROTATE = 'ROTATE',
    BREATHE = 'BREATHE',
    WAVE = 'WAVE',
    SHIMMER = 'SHIMMER',
    SPARK = 'SPARK'
}

/**
 * Etkileşim durumu
 */
export enum ArcState {
    IDLE = 'IDLE',
    HOVER = 'HOVER',
    SELECTED = 'SELECTED',
    DRAGGING = 'DRAGGING',
    DISABLED = 'DISABLED',
    HIGHLIGHTED = 'HIGHLIGHTED'
}

/**
 * Yay konfigürasyonu
 */
export interface ArcConfig {
    // Geometri
    center: THREE.Vector3;
    radius: number;
    startAngle: number;
    endAngle: number;
    normal?: THREE.Vector3;

    // Stil
    style: ArcStyleType;
    color: number;
    secondaryColor?: number;
    lineWidth: number;
    opacity: number;

    // Animasyon
    animation: ArcAnimationType;
    animationSpeed: number;
    animationIntensity: number;

    // Açı gösterimi
    showAngle: boolean;
    angleUnit: AngleUnit;
    anglePrecision: number;

    // Etiket
    showLabel: boolean;
    labelText?: string;
    labelOffset: number;
    labelSize: number;

    // Oklar
    showStartArrow: boolean;
    showEndArrow: boolean;
    arrowSize: number;

    // Efektler
    enableGlow: boolean;
    glowIntensity: number;
    glowColor?: number;

    // Segment sayısı (kalite)
    segments: number;
}

/**
 * Tema renk paleti
 */
export interface ArcThemeColors {
    primary: number;
    secondary: number;
    accent: number;
    glow: number;
    text: number;
    background: number;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_ARC_CONFIG: ArcConfig = {
    center: new THREE.Vector3(0, 0, 0),
    radius: 1,
    startAngle: 0,
    endAngle: Math.PI / 2,
    normal: new THREE.Vector3(0, 0, 1),

    style: ArcStyleType.SOLID,
    color: 0x00ff88,
    lineWidth: 2,
    opacity: 1,

    animation: ArcAnimationType.NONE,
    animationSpeed: 1,
    animationIntensity: 0.5,

    showAngle: true,
    angleUnit: AngleUnit.DEGREES,
    anglePrecision: 1,

    showLabel: true,
    labelOffset: 0.15,
    labelSize: 0.5,

    showStartArrow: true,
    showEndArrow: true,
    arrowSize: 0.1,

    enableGlow: false,
    glowIntensity: 0.5,

    segments: 64
};

// Tema paleti
const ARC_THEMES: Record<string, ArcThemeColors> = {
    NEON: {
        primary: 0x00ff88,
        secondary: 0x00ffff,
        accent: 0xff00ff,
        glow: 0x00ff88,
        text: 0xffffff,
        background: 0x001122
    },
    HOLOGRAPHIC: {
        primary: 0x88ccff,
        secondary: 0xff88cc,
        accent: 0xccff88,
        glow: 0xaaddff,
        text: 0xffffff,
        background: 0x112233
    },
    CATIA: {
        primary: 0x00aa00,
        secondary: 0x008800,
        accent: 0x00cc00,
        glow: 0x00ff00,
        text: 0x000000,
        background: 0xcccccc
    },
    SOLIDWORKS: {
        primary: 0xff6600,
        secondary: 0xff8800,
        accent: 0xffaa00,
        glow: 0xffcc00,
        text: 0x000000,
        background: 0xf0f0f0
    },
    BLUEPRINT: {
        primary: 0x4488ff,
        secondary: 0x2266dd,
        accent: 0x66aaff,
        glow: 0x88ccff,
        text: 0xffffff,
        background: 0x001144
    },
    FIRE: {
        primary: 0xff4400,
        secondary: 0xff8800,
        accent: 0xffcc00,
        glow: 0xff6600,
        text: 0xffffff,
        background: 0x220000
    },
    ICE: {
        primary: 0x00ccff,
        secondary: 0x88ddff,
        accent: 0xaaeeff,
        glow: 0x66ddff,
        text: 0xffffff,
        background: 0x001133
    },
    MATRIX: {
        primary: 0x00ff00,
        secondary: 0x00cc00,
        accent: 0x00ff44,
        glow: 0x00ff00,
        text: 0x00ff00,
        background: 0x000000
    }
};

// ============================================================================
// SHADER DEFINITIONS
// ============================================================================

const ARC_GLOW_VERTEX_SHADER = `
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const ARC_GLOW_FRAGMENT_SHADER = `
    uniform vec3 glowColor;
    uniform float glowIntensity;
    uniform float time;
    uniform float pulseSpeed;

    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
        float pulse = sin(time * pulseSpeed) * 0.5 + 0.5;
        float glow = glowIntensity * (0.7 + pulse * 0.3);

        // Kenardan merkeze glow efekti
        float edgeFade = 1.0 - abs(vUv.y - 0.5) * 2.0;
        edgeFade = pow(edgeFade, 0.5);

        vec3 finalColor = glowColor * glow * edgeFade;
        float alpha = edgeFade * glow;

        gl_FragColor = vec4(finalColor, alpha);
    }
`;

const ARC_ANIMATED_VERTEX_SHADER = `
    attribute float progress;

    varying float vProgress;
    varying vec2 vUv;

    void main() {
        vProgress = progress;
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const ARC_ANIMATED_FRAGMENT_SHADER = `
    uniform vec3 color1;
    uniform vec3 color2;
    uniform float time;
    uniform float flowSpeed;
    uniform int animationType;

    varying float vProgress;
    varying vec2 vUv;

    void main() {
        float t = vProgress;
        vec3 finalColor;
        float alpha = 1.0;

        if (animationType == 0) {
            // Flow animation
            float flow = fract(t - time * flowSpeed);
            finalColor = mix(color1, color2, flow);
        } else if (animationType == 1) {
            // Pulse animation
            float pulse = sin(time * flowSpeed * 2.0) * 0.5 + 0.5;
            finalColor = mix(color1, color2, pulse);
            alpha = 0.5 + pulse * 0.5;
        } else if (animationType == 2) {
            // Sweep animation
            float sweep = step(fract(time * flowSpeed * 0.5), t);
            finalColor = mix(color1, color2, sweep);
            alpha = 0.3 + sweep * 0.7;
        } else {
            // Default gradient
            finalColor = mix(color1, color2, t);
        }

        gl_FragColor = vec4(finalColor, alpha);
    }
`;

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoMeasurementArc - Gelişmiş 3D Yay Ölçüm Sistemi
 */
export class FikoMeasurementArc extends THREE.Group {

    // Konfigürasyon
    private config: ArcConfig;
    private theme: ArcThemeColors;

    // Bileşenler
    private arcLine: Line2 | THREE.Line | null = null;
    private glowMesh: THREE.Mesh | null = null;
    private labelSprite: THREE.Sprite | null = null;
    private startArrow: THREE.Mesh | null = null;
    private endArrow: THREE.Mesh | null = null;
    private sectorMesh: THREE.Mesh | null = null;
    private extensionLines: THREE.Line[] = [];

    // Animasyon
    private animationId: number = 0;
    private isAnimating: boolean = false;
    private animationStartTime: number = 0;
    private glowMaterial: THREE.ShaderMaterial | null = null;
    private animatedMaterial: THREE.ShaderMaterial | null = null;

    // Durum
    private currentState: ArcState = ArcState.IDLE;
    private isVisible: boolean = true;

    // Cache
    private static geometryCache: Map<string, THREE.BufferGeometry> = new Map();
    private static materialCache: Map<string, THREE.Material> = new Map();

    // Reusable objects
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempVec3_2 = new THREE.Vector3();
    private static readonly _tempQuaternion = new THREE.Quaternion();
    private static readonly _tempMatrix = new THREE.Matrix4();

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(config: Partial<ArcConfig> = {}) {
        super();

        this.config = { ...DEFAULT_ARC_CONFIG, ...config };
        this.theme = ARC_THEMES.NEON;

        this.name = 'FikoMeasurementArc';
        this.userData.type = 'measurement-arc';
        this.userData.selectable = true;
        this.userData.hoverable = true;

        this.buildArc();

        if (this.config.animation !== ArcAnimationType.NONE) {
            this.startAnimation();
        }
    }

    // ========================================================================
    // PUBLIC API
    // ========================================================================

    /**
     * Yay konfigürasyonunu güncelle
     */
    public updateConfig(newConfig: Partial<ArcConfig>): void {
        this.config = { ...this.config, ...newConfig };
        this.rebuild();
    }

    /**
     * Açı değerlerini güncelle
     */
    public setAngles(startAngle: number, endAngle: number): void {
        this.config.startAngle = startAngle;
        this.config.endAngle = endAngle;
        this.rebuild();
    }

    /**
     * Yarıçapı güncelle
     */
    public setRadius(radius: number): void {
        this.config.radius = radius;
        this.rebuild();
    }

    /**
     * Merkezi güncelle
     */
    public setCenter(center: THREE.Vector3): void {
        this.config.center.copy(center);
        this.position.copy(center);
    }

    /**
     * Normal vektörü güncelle (yay düzlemi)
     */
    public setNormal(normal: THREE.Vector3): void {
        this.config.normal = normal.clone().normalize();
        this.rebuild();
    }

    /**
     * Stili değiştir
     */
    public setStyle(style: ArcStyleType): void {
        this.config.style = style;
        this.rebuild();
    }

    /**
     * Tema uygula
     */
    public applyTheme(themeName: string): void {
        if (ARC_THEMES[themeName]) {
            this.theme = ARC_THEMES[themeName];
            this.config.color = this.theme.primary;
            this.config.secondaryColor = this.theme.secondary;
            this.config.glowColor = this.theme.glow;
            this.rebuild();
        }
    }

    /**
     * Özel tema ayarla
     */
    public setCustomTheme(theme: ArcThemeColors): void {
        this.theme = theme;
        this.config.color = theme.primary;
        this.config.secondaryColor = theme.secondary;
        this.config.glowColor = theme.glow;
        this.rebuild();
    }

    /**
     * Durum değiştir
     */
    public setState(state: ArcState): void {
        if (this.currentState === state) return;

        this.currentState = state;
        this.applyStateVisuals();
    }

    /**
     * Açı değerini al (formatlanmış)
     */
    public getFormattedAngle(): string {
        const angleDiff = Math.abs(this.config.endAngle - this.config.startAngle);

        switch (this.config.angleUnit) {
            case AngleUnit.DEGREES:
                return `${(angleDiff * 180 / Math.PI).toFixed(this.config.anglePrecision)}°`;
            case AngleUnit.RADIANS:
                return `${angleDiff.toFixed(this.config.anglePrecision)} rad`;
            case AngleUnit.GRADIANS:
                return `${(angleDiff * 200 / Math.PI).toFixed(this.config.anglePrecision)} grad`;
            case AngleUnit.TURNS:
                return `${(angleDiff / (2 * Math.PI)).toFixed(this.config.anglePrecision)} turn`;
            default:
                return `${(angleDiff * 180 / Math.PI).toFixed(this.config.anglePrecision)}°`;
        }
    }

    /**
     * Yay uzunluğunu al
     */
    public getArcLength(): number {
        const angleDiff = Math.abs(this.config.endAngle - this.config.startAngle);
        return this.config.radius * angleDiff;
    }

    /**
     * Animasyonu başlat
     */
    public startAnimation(): void {
        if (this.isAnimating) return;

        this.isAnimating = true;
        this.animationStartTime = performance.now();
        this.animate();
    }

    /**
     * Animasyonu durdur
     */
    public stopAnimation(): void {
        this.isAnimating = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = 0;
        }
    }

    /**
     * Glow efektini aç/kapat
     */
    public setGlow(enabled: boolean, intensity?: number): void {
        this.config.enableGlow = enabled;
        if (intensity !== undefined) {
            this.config.glowIntensity = intensity;
        }

        if (this.glowMesh) {
            this.glowMesh.visible = enabled;
            if (this.glowMaterial) {
                this.glowMaterial.uniforms.glowIntensity.value = this.config.glowIntensity;
            }
        }
    }

    /**
     * Görünürlüğü ayarla
     */
    public setVisibility(visible: boolean): void {
        this.isVisible = visible;
        this.visible = visible;
    }

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.dispose();
        this.buildArc();

        if (this.config.animation !== ArcAnimationType.NONE) {
            this.startAnimation();
        }
    }

    /**
     * Kamerayı güncelle (camera-aware rendering için)
     */
    public updateCamera(camera: THREE.Camera): void {
        if (this.labelSprite) {
            // Label her zaman kameraya baksın
            this.labelSprite.lookAt(camera.position);

            // Mesafeye göre ölçekleme
            const distance = camera.position.distanceTo(this.position);
            const scale = Math.max(0.5, Math.min(2, distance * 0.1));
            this.labelSprite.scale.setScalar(this.config.labelSize * scale);
        }
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.stopAnimation();

        // Bileşenleri temizle
        if (this.arcLine) {
            if (this.arcLine.geometry) this.arcLine.geometry.dispose();
            if ((this.arcLine as any).material) (this.arcLine as any).material.dispose();
            this.remove(this.arcLine);
            this.arcLine = null;
        }

        if (this.glowMesh) {
            if (this.glowMesh.geometry) this.glowMesh.geometry.dispose();
            if (this.glowMesh.material) (this.glowMesh.material as THREE.Material).dispose();
            this.remove(this.glowMesh);
            this.glowMesh = null;
        }

        if (this.labelSprite) {
            if ((this.labelSprite.material as THREE.SpriteMaterial).map) {
                (this.labelSprite.material as THREE.SpriteMaterial).map!.dispose();
            }
            (this.labelSprite.material as THREE.Material).dispose();
            this.remove(this.labelSprite);
            this.labelSprite = null;
        }

        if (this.startArrow) {
            if (this.startArrow.geometry) this.startArrow.geometry.dispose();
            if (this.startArrow.material) (this.startArrow.material as THREE.Material).dispose();
            this.remove(this.startArrow);
            this.startArrow = null;
        }

        if (this.endArrow) {
            if (this.endArrow.geometry) this.endArrow.geometry.dispose();
            if (this.endArrow.material) (this.endArrow.material as THREE.Material).dispose();
            this.remove(this.endArrow);
            this.endArrow = null;
        }

        if (this.sectorMesh) {
            if (this.sectorMesh.geometry) this.sectorMesh.geometry.dispose();
            if (this.sectorMesh.material) (this.sectorMesh.material as THREE.Material).dispose();
            this.remove(this.sectorMesh);
            this.sectorMesh = null;
        }

        this.extensionLines.forEach(line => {
            if (line.geometry) line.geometry.dispose();
            if (line.material) (line.material as THREE.Material).dispose();
            this.remove(line);
        });
        this.extensionLines = [];

        this.glowMaterial = null;
        this.animatedMaterial = null;
    }

    // ========================================================================
    // PRIVATE METHODS - BUILD
    // ========================================================================

    /**
     * Yay bileşenlerini oluştur
     */
    private buildArc(): void {
        this.position.copy(this.config.center);

        // Yay geometrisi oluştur
        this.createArcLine();

        // Glow efekti
        if (this.config.enableGlow) {
            this.createGlowEffect();
        }

        // Oklar
        if (this.config.showStartArrow) {
            this.createArrow(true);
        }
        if (this.config.showEndArrow) {
            this.createArrow(false);
        }

        // Etiket
        if (this.config.showLabel || this.config.showAngle) {
            this.createLabel();
        }

        // Extension lines (merkeze)
        this.createExtensionLines();

        // Normal'a göre döndür
        this.applyNormalRotation();
    }

    /**
     * Ana yay çizgisini oluştur
     */
    private createArcLine(): void {
        const points = this.generateArcPoints();

        switch (this.config.style) {
            case ArcStyleType.SOLID:
            case ArcStyleType.CATIA_STANDARD:
            case ArcStyleType.SOLIDWORKS_STANDARD:
            case ArcStyleType.AUTOCAD_STANDARD:
                this.createSolidArc(points);
                break;

            case ArcStyleType.DASHED:
            case ArcStyleType.CENTERLINE:
            case ArcStyleType.PHANTOM:
                this.createDashedArc(points);
                break;

            case ArcStyleType.DOTTED:
                this.createDottedArc(points);
                break;

            case ArcStyleType.GRADIENT_LINEAR:
            case ArcStyleType.GRADIENT_RADIAL:
            case ArcStyleType.GRADIENT_RAINBOW:
                this.createGradientArc(points);
                break;

            case ArcStyleType.ANIMATED_FLOW:
            case ArcStyleType.ANIMATED_PULSE:
            case ArcStyleType.ANIMATED_SWEEP:
                this.createAnimatedArc(points);
                break;

            case ArcStyleType.NEON_GLOW:
            case ArcStyleType.HOLOGRAPHIC:
            case ArcStyleType.PLASMA:
            case ArcStyleType.ELECTRIC:
            case ArcStyleType.LASER:
                this.createEffectArc(points);
                break;

            case ArcStyleType.WAVE_SINE:
            case ArcStyleType.WAVE_SQUARE:
            case ArcStyleType.ZIGZAG:
                this.createWaveArc(points);
                break;

            default:
                this.createSolidArc(points);
        }
    }

    /**
     * Yay noktalarını oluştur
     */
    private generateArcPoints(): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        const segments = this.config.segments;
        const startAngle = this.config.startAngle;
        const endAngle = this.config.endAngle;
        const radius = this.config.radius;

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const angle = startAngle + (endAngle - startAngle) * t;

            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;

            points.push(new THREE.Vector3(x, y, 0));
        }

        return points;
    }

    /**
     * Solid yay oluştur
     */
    private createSolidArc(points: THREE.Vector3[]): void {
        const positions: number[] = [];
        points.forEach(p => positions.push(p.x, p.y, p.z));

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: this.config.color,
            linewidth: this.config.lineWidth * 0.001,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            resolution: getCanvasResolutionVec2()
        });

        this.arcLine = new Line2(geometry, material);
        this.arcLine.computeLineDistances();
        this.add(this.arcLine);
    }

    /**
     * Dashed yay oluştur
     */
    private createDashedArc(points: THREE.Vector3[]): void {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        let dashSize = 0.1;
        let gapSize = 0.05;

        // Stil bazlı dash ayarları
        if (this.config.style === ArcStyleType.CENTERLINE) {
            dashSize = 0.15;
            gapSize = 0.03;
        } else if (this.config.style === ArcStyleType.PHANTOM) {
            dashSize = 0.2;
            gapSize = 0.02;
        }

        const material = new THREE.LineDashedMaterial({
            color: this.config.color,
            linewidth: this.config.lineWidth,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            dashSize: dashSize * this.config.radius,
            gapSize: gapSize * this.config.radius
        });

        this.arcLine = new THREE.Line(geometry, material);
        this.arcLine.computeLineDistances();
        this.add(this.arcLine);
    }

    /**
     * Dotted yay oluştur
     */
    private createDottedArc(points: THREE.Vector3[]): void {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        const material = new THREE.LineDashedMaterial({
            color: this.config.color,
            linewidth: this.config.lineWidth,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            dashSize: 0.02 * this.config.radius,
            gapSize: 0.04 * this.config.radius
        });

        this.arcLine = new THREE.Line(geometry, material);
        this.arcLine.computeLineDistances();
        this.add(this.arcLine);
    }

    /**
     * Gradient yay oluştur
     */
    private createGradientArc(points: THREE.Vector3[]): void {
        const positions: number[] = [];
        const colors: number[] = [];

        const color1 = new THREE.Color(this.config.color);
        const color2 = new THREE.Color(this.config.secondaryColor || this.theme.secondary);

        points.forEach((p, i) => {
            positions.push(p.x, p.y, p.z);

            const t = i / (points.length - 1);
            let color: THREE.Color;

            if (this.config.style === ArcStyleType.GRADIENT_RAINBOW) {
                // Rainbow gradient
                color = new THREE.Color().setHSL(t, 1, 0.5);
            } else {
                // Linear gradient
                color = color1.clone().lerp(color2, t);
            }

            colors.push(color.r, color.g, color.b);
        });

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        const material = new THREE.LineBasicMaterial({
            vertexColors: true,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            linewidth: this.config.lineWidth
        });

        this.arcLine = new THREE.Line(geometry, material);
        this.add(this.arcLine);
    }

    /**
     * Animasyonlu yay oluştur
     */
    private createAnimatedArc(points: THREE.Vector3[]): void {
        const positions: number[] = [];
        const progress: number[] = [];

        points.forEach((p, i) => {
            positions.push(p.x, p.y, p.z);
            progress.push(i / (points.length - 1));
        });

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('progress', new THREE.Float32BufferAttribute(progress, 1));

        let animationType = 0;
        if (this.config.style === ArcStyleType.ANIMATED_PULSE) animationType = 1;
        if (this.config.style === ArcStyleType.ANIMATED_SWEEP) animationType = 2;

        this.animatedMaterial = new THREE.ShaderMaterial({
            uniforms: {
                color1: { value: new THREE.Color(this.config.color) },
                color2: { value: new THREE.Color(this.config.secondaryColor || this.theme.secondary) },
                time: { value: 0 },
                flowSpeed: { value: this.config.animationSpeed },
                animationType: { value: animationType }
            },
            vertexShader: ARC_ANIMATED_VERTEX_SHADER,
            fragmentShader: ARC_ANIMATED_FRAGMENT_SHADER,
            transparent: true
        });

        this.arcLine = new THREE.Line(geometry, this.animatedMaterial);
        this.add(this.arcLine);
    }

    /**
     * Efektli yay oluştur
     */
    private createEffectArc(points: THREE.Vector3[]): void {
        // Ana çizgi
        this.createSolidArc(points);

        // Ek efektler
        if (this.config.style === ArcStyleType.NEON_GLOW ||
            this.config.style === ArcStyleType.HOLOGRAPHIC) {
            this.config.enableGlow = true;
        }

        if (this.config.style === ArcStyleType.ELECTRIC) {
            this.createElectricEffect(points);
        }

        if (this.config.style === ArcStyleType.PLASMA) {
            this.createPlasmaEffect(points);
        }
    }

    /**
     * Wave yay oluştur
     */
    private createWaveArc(points: THREE.Vector3[]): void {
        const wavePoints: THREE.Vector3[] = [];
        const waveFrequency = 20;
        const waveAmplitude = 0.02 * this.config.radius;

        points.forEach((p, i) => {
            const t = i / (points.length - 1);
            const angle = Math.atan2(p.y, p.x);
            const normalX = -Math.sin(angle);
            const normalY = Math.cos(angle);

            let offset = 0;

            if (this.config.style === ArcStyleType.WAVE_SINE) {
                offset = Math.sin(t * Math.PI * waveFrequency) * waveAmplitude;
            } else if (this.config.style === ArcStyleType.WAVE_SQUARE) {
                offset = (Math.sin(t * Math.PI * waveFrequency) > 0 ? 1 : -1) * waveAmplitude;
            } else if (this.config.style === ArcStyleType.ZIGZAG) {
                const phase = (t * waveFrequency) % 1;
                offset = (phase < 0.5 ? phase * 4 - 1 : 3 - phase * 4) * waveAmplitude;
            }

            wavePoints.push(new THREE.Vector3(
                p.x + normalX * offset,
                p.y + normalY * offset,
                p.z
            ));
        });

        this.createSolidArc(wavePoints);
    }

    /**
     * Electric efekti
     */
    private createElectricEffect(points: THREE.Vector3[]): void {
        // Random zigzag overlay
        const electricPoints: THREE.Vector3[] = [];
        const segments = Math.floor(points.length / 4);

        for (let i = 0; i < segments; i++) {
            const startIdx = Math.floor(i * points.length / segments);
            const endIdx = Math.floor((i + 1) * points.length / segments);

            electricPoints.push(points[startIdx].clone());

            // Random offset in middle
            const midIdx = Math.floor((startIdx + endIdx) / 2);
            const midPoint = points[midIdx].clone();
            const angle = Math.atan2(midPoint.y, midPoint.x);
            const normalX = -Math.sin(angle);
            const normalY = Math.cos(angle);
            const randomOffset = (Math.random() - 0.5) * 0.05 * this.config.radius;

            electricPoints.push(new THREE.Vector3(
                midPoint.x + normalX * randomOffset,
                midPoint.y + normalY * randomOffset,
                midPoint.z
            ));
        }
        electricPoints.push(points[points.length - 1].clone());

        const geometry = new THREE.BufferGeometry().setFromPoints(electricPoints);
        const material = new THREE.LineBasicMaterial({
            color: this.theme.accent,
            opacity: 0.6,
            transparent: true
        });

        const electricLine = new THREE.Line(geometry, material);
        this.add(electricLine);
    }

    /**
     * Plasma efekti
     */
    private createPlasmaEffect(points: THREE.Vector3[]): void {
        // Multiple overlapping lines with different colors
        const colors = [this.theme.primary, this.theme.secondary, this.theme.accent];

        colors.forEach((color, index) => {
            const offsetPoints = points.map(p => {
                const offset = (index - 1) * 0.005 * this.config.radius;
                const angle = Math.atan2(p.y, p.x);
                return new THREE.Vector3(
                    p.x - Math.sin(angle) * offset,
                    p.y + Math.cos(angle) * offset,
                    p.z
                );
            });

            const geometry = new THREE.BufferGeometry().setFromPoints(offsetPoints);
            const material = new THREE.LineBasicMaterial({
                color: color,
                opacity: 0.5 - index * 0.1,
                transparent: true
            });

            const plasmaLine = new THREE.Line(geometry, material);
            this.add(plasmaLine);
        });
    }

    /**
     * Glow efekti oluştur
     */
    private createGlowEffect(): void {
        const points = this.generateArcPoints();

        // Glow için daha kalın bir tüp oluştur
        const curve = new THREE.CatmullRomCurve3(points);
        const tubeGeometry = new THREE.TubeGeometry(
            curve,
            this.config.segments,
            this.config.lineWidth * 0.01,
            8,
            false
        );

        this.glowMaterial = new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(this.config.glowColor || this.theme.glow) },
                glowIntensity: { value: this.config.glowIntensity },
                time: { value: 0 },
                pulseSpeed: { value: this.config.animationSpeed }
            },
            vertexShader: ARC_GLOW_VERTEX_SHADER,
            fragmentShader: ARC_GLOW_FRAGMENT_SHADER,
            transparent: true,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
            depthWrite: false
        });

        this.glowMesh = new THREE.Mesh(tubeGeometry, this.glowMaterial);
        this.add(this.glowMesh);
    }

    /**
     * Ok oluştur
     */
    private createArrow(isStart: boolean): void {
        const size = this.config.arrowSize * this.config.radius;

        // Ok geometrisi
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-size * 2, -size * 0.6);
        shape.lineTo(-size * 1.5, 0);
        shape.lineTo(-size * 2, size * 0.6);
        shape.closePath();

        const geometry = new THREE.ShapeGeometry(shape);
        const material = new THREE.MeshBasicMaterial({
            color: this.config.color,
            opacity: this.config.opacity,
            transparent: this.config.opacity < 1,
            side: THREE.DoubleSide
        });

        const arrow = new THREE.Mesh(geometry, material);

        // Pozisyon ve rotasyon
        const angle = isStart ? this.config.startAngle : this.config.endAngle;
        const x = Math.cos(angle) * this.config.radius;
        const y = Math.sin(angle) * this.config.radius;

        arrow.position.set(x, y, 0);

        // Teğet yönünde döndür
        const tangentAngle = isStart ? angle + Math.PI / 2 : angle - Math.PI / 2;
        arrow.rotation.z = tangentAngle;

        if (isStart) {
            this.startArrow = arrow;
        } else {
            this.endArrow = arrow;
        }

        this.add(arrow);
    }

    /**
     * Etiket oluştur
     */
    private createLabel(): void {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d')!;
        canvas.width = 256;
        canvas.height = 128;

        // Arka plan
        context.fillStyle = `rgba(${(this.theme.background >> 16) & 255}, ${(this.theme.background >> 8) & 255}, ${this.theme.background & 255}, 0.8)`;
        context.beginPath();
        context.roundRect(10, 10, canvas.width - 20, canvas.height - 20, 10);
        context.fill();

        // Çerçeve
        context.strokeStyle = `#${this.theme.primary.toString(16).padStart(6, '0')}`;
        context.lineWidth = 2;
        context.stroke();

        // Metin
        const text = this.config.labelText || this.getFormattedAngle();
        context.font = 'bold 32px Arial';
        context.fillStyle = `#${this.theme.text.toString(16).padStart(6, '0')}`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(text, canvas.width / 2, canvas.height / 2);

        // Alt metin (yay uzunluğu)
        if (this.config.showLabel) {
            context.font = '18px Arial';
            context.fillStyle = `#${this.theme.secondary.toString(16).padStart(6, '0')}`;
            context.fillText(`L: ${this.getArcLength().toFixed(2)}`, canvas.width / 2, canvas.height / 2 + 25);
        }

        const texture = new THREE.CanvasTexture(canvas);
        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true
        });

        this.labelSprite = new THREE.Sprite(spriteMaterial);

        // Etiket pozisyonu (yay ortası)
        const midAngle = (this.config.startAngle + this.config.endAngle) / 2;
        const labelRadius = this.config.radius * (1 + this.config.labelOffset);

        this.labelSprite.position.set(
            Math.cos(midAngle) * labelRadius,
            Math.sin(midAngle) * labelRadius,
            0
        );

        this.labelSprite.scale.set(this.config.labelSize, this.config.labelSize * 0.5, 1);

        this.add(this.labelSprite);
    }

    /**
     * Uzatma çizgileri oluştur (merkeze)
     */
    private createExtensionLines(): void {
        const extensionLength = this.config.radius * 0.1;

        // Başlangıç uzatma çizgisi
        const startAngle = this.config.startAngle;
        const startInner = new THREE.Vector3(0, 0, 0);
        const startOuter = new THREE.Vector3(
            Math.cos(startAngle) * (this.config.radius + extensionLength),
            Math.sin(startAngle) * (this.config.radius + extensionLength),
            0
        );

        const startGeometry = new THREE.BufferGeometry().setFromPoints([startInner, startOuter]);
        const startMaterial = new THREE.LineDashedMaterial({
            color: this.config.color,
            opacity: 0.5,
            transparent: true,
            dashSize: 0.02 * this.config.radius,
            gapSize: 0.02 * this.config.radius
        });

        const startLine = new THREE.Line(startGeometry, startMaterial);
        startLine.computeLineDistances();
        this.extensionLines.push(startLine);
        this.add(startLine);

        // Bitiş uzatma çizgisi
        const endAngle = this.config.endAngle;
        const endInner = new THREE.Vector3(0, 0, 0);
        const endOuter = new THREE.Vector3(
            Math.cos(endAngle) * (this.config.radius + extensionLength),
            Math.sin(endAngle) * (this.config.radius + extensionLength),
            0
        );

        const endGeometry = new THREE.BufferGeometry().setFromPoints([endInner, endOuter]);
        const endMaterial = new THREE.LineDashedMaterial({
            color: this.config.color,
            opacity: 0.5,
            transparent: true,
            dashSize: 0.02 * this.config.radius,
            gapSize: 0.02 * this.config.radius
        });

        const endLine = new THREE.Line(endGeometry, endMaterial);
        endLine.computeLineDistances();
        this.extensionLines.push(endLine);
        this.add(endLine);
    }

    /**
     * Normal vektöre göre döndür
     */
    private applyNormalRotation(): void {
        if (!this.config.normal) return;

        const defaultNormal = new THREE.Vector3(0, 0, 1);
        const targetNormal = this.config.normal.clone().normalize();

        FikoMeasurementArc._tempQuaternion.setFromUnitVectors(defaultNormal, targetNormal);
        this.quaternion.copy(FikoMeasurementArc._tempQuaternion);
    }

    // ========================================================================
    // PRIVATE METHODS - ANIMATION
    // ========================================================================

    /**
     * Animasyon döngüsü
     */
    private animate = (): void => {
        if (!this.isAnimating) return;

        const elapsed = (performance.now() - this.animationStartTime) / 1000;

        // Glow animasyonu
        if (this.glowMaterial) {
            this.glowMaterial.uniforms.time.value = elapsed;
        }

        // Ana çizgi animasyonu
        if (this.animatedMaterial) {
            this.animatedMaterial.uniforms.time.value = elapsed;
        }

        // Özel animasyonlar
        this.updateCustomAnimations(elapsed);

        this.animationId = requestAnimationFrame(this.animate);
    };

    /**
     * Özel animasyonları güncelle
     */
    private updateCustomAnimations(elapsed: number): void {
        const intensity = this.config.animationIntensity;
        const speed = this.config.animationSpeed;

        switch (this.config.animation) {
            case ArcAnimationType.PULSE:
                if (this.arcLine && (this.arcLine as any).material) {
                    const pulse = Math.sin(elapsed * speed * 2) * 0.5 + 0.5;
                    ((this.arcLine as any).material as any).opacity = 0.5 + pulse * 0.5 * intensity;
                }
                break;

            case ArcAnimationType.BREATHE:
                const breathe = Math.sin(elapsed * speed) * 0.5 + 0.5;
                const scale = 1 + breathe * 0.1 * intensity;
                this.scale.setScalar(scale);
                break;

            case ArcAnimationType.SHIMMER:
                if (this.arcLine && (this.arcLine as any).material) {
                    const shimmer = Math.sin(elapsed * speed * 5) * 0.3 + 0.7;
                    ((this.arcLine as any).material as any).opacity = shimmer * intensity;
                }
                break;

            case ArcAnimationType.SPARK:
                // Random brightness spikes
                if (Math.random() < 0.05 && this.glowMaterial) {
                    this.glowMaterial.uniforms.glowIntensity.value =
                        this.config.glowIntensity * (1 + Math.random() * intensity);

                    setTimeout(() => {
                        if (this.glowMaterial) {
                            this.glowMaterial.uniforms.glowIntensity.value = this.config.glowIntensity;
                        }
                    }, 50);
                }
                break;
        }
    }

    // ========================================================================
    // PRIVATE METHODS - STATE
    // ========================================================================

    /**
     * Durum görsellerini uygula
     */
    private applyStateVisuals(): void {
        switch (this.currentState) {
            case ArcState.HOVER:
                this.setGlow(true, this.config.glowIntensity * 1.5);
                this.scale.setScalar(1.05);
                break;

            case ArcState.SELECTED:
                this.setGlow(true, this.config.glowIntensity * 2);
                if (this.arcLine && (this.arcLine as any).material) {
                    ((this.arcLine as any).material as any).color = new THREE.Color(this.theme.accent);
                }
                break;

            case ArcState.DRAGGING:
                this.setGlow(true, this.config.glowIntensity);
                this.scale.setScalar(1.1);
                break;

            case ArcState.DISABLED:
                this.setGlow(false);
                if (this.arcLine && (this.arcLine as any).material) {
                    ((this.arcLine as any).material as any).opacity = 0.3;
                }
                break;

            case ArcState.HIGHLIGHTED:
                this.setGlow(true, this.config.glowIntensity * 2.5);
                break;

            case ArcState.IDLE:
            default:
                this.setGlow(this.config.enableGlow, this.config.glowIntensity);
                this.scale.setScalar(1);
                if (this.arcLine && (this.arcLine as any).material) {
                    ((this.arcLine as any).material as any).color = new THREE.Color(this.config.color);
                    ((this.arcLine as any).material as any).opacity = this.config.opacity;
                }
                break;
        }
    }

    // ========================================================================
    // STATIC FACTORY METHODS
    // ========================================================================

    /**
     * Açı ölçümü oluştur
     */
    public static createAngleMeasurement(
        center: THREE.Vector3,
        point1: THREE.Vector3,
        point2: THREE.Vector3,
        config: Partial<ArcConfig> = {}
    ): FikoMeasurementArc {
        const dir1 = point1.clone().sub(center).normalize();
        const dir2 = point2.clone().sub(center).normalize();

        const startAngle = Math.atan2(dir1.y, dir1.x);
        const endAngle = Math.atan2(dir2.y, dir2.x);

        const distance = Math.min(
            center.distanceTo(point1),
            center.distanceTo(point2)
        );

        return new FikoMeasurementArc({
            center,
            radius: distance * 0.3,
            startAngle,
            endAngle,
            ...config
        });
    }

    /**
     * Yay uzunluğu ölçümü oluştur
     */
    public static createArcLengthMeasurement(
        center: THREE.Vector3,
        radius: number,
        startAngle: number,
        endAngle: number,
        config: Partial<ArcConfig> = {}
    ): FikoMeasurementArc {
        return new FikoMeasurementArc({
            center,
            radius,
            startAngle,
            endAngle,
            showAngle: false,
            showLabel: true,
            ...config
        });
    }

    /**
     * 90 derece açı işareti oluştur
     */
    public static createRightAngleMarker(
        center: THREE.Vector3,
        dir1: THREE.Vector3,
        dir2: THREE.Vector3,
        size: number = 0.1,
        config: Partial<ArcConfig> = {}
    ): FikoMeasurementArc {
        // Kare işareti için özel bir arc
        const arc = new FikoMeasurementArc({
            center,
            radius: size,
            startAngle: Math.atan2(dir1.y, dir1.x),
            endAngle: Math.atan2(dir2.y, dir2.x),
            showAngle: false,
            showLabel: false,
            showStartArrow: false,
            showEndArrow: false,
            style: ArcStyleType.SOLID,
            segments: 4, // Kare için 4 segment
            ...config
        });

        return arc;
    }

    /**
     * Preset tema ile oluştur
     */
    public static createWithTheme(
        themeName: string,
        config: Partial<ArcConfig> = {}
    ): FikoMeasurementArc {
        const arc = new FikoMeasurementArc(config);
        arc.applyTheme(themeName);
        return arc;
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    ARC_THEMES,
    DEFAULT_ARC_CONFIG
};
